import * as React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import { PrimaryButton } from '@fluentui/react';
import { ServiceStatusLabel, ServiceTypLabel } from 'utils/labels';
import { formStyles } from 'styles/formStyles';
import { Service } from 'api/service';
import { Timestamps } from 'formComponents/Timestamps';
import { ServicePosition } from 'api/servicePosition';
import { Termin } from 'api/termin';
import {
    betreuerByNrSelector,
    skontodefinitionenByArtSelector,
    textbausteineByKzNrSelector,
} from 'store/lookups/selector';
import { formatMoney, formatPercent, formatSmDate } from 'utils/formatter';
import { authSelector } from 'store/auth/selector';
import nl2br from 'react-nl2br';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { ServicePositionen } from './ServicePositionen';

const styles = mergeStyleSets({
    shortLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '120px 120px 1fr 1fr  100px max-content',
        gridColumnGap: 10,
    },
    status: {
        textAlign: 'right',
    },
});

interface ServiceItemProps {
    service: Service;
    servicePositionen: ServicePosition[];
    wiedervorlagen: Termin[];
    withKunde?: boolean;
    viewMode: 'short' | 'long';
    withHeader?: boolean;
    editService: () => void;
    refetch: () => void;
}

export const ServiceItem: React.FC<ServiceItemProps> = ({
    service,
    servicePositionen,
    wiedervorlagen,
    withKunde,
    viewMode,
    withHeader = true,
    editService,
    refetch,
}) => {
    const { kunde } = service;

    const auth = useSelector(authSelector);
    const betreuerByNr = useSelector(betreuerByNrSelector);
    const textbausteineByKzNr = useSelector(textbausteineByKzNrSelector);
    const skontodefinitionenByArt = useSelector(skontodefinitionenByArtSelector);

    const history = useHistory();

    if (!kunde) {
        return null;
    }

    const renderTimestamps = () => (
        <Timestamps
            createdDate={service.anlageDat}
            createdDecimalTime={service.anlageUhr}
            createdUser={service.anlageUser}
            modifiedDate={service.updDat}
            modifiedDecimalTime={service.updUhr}
            modifiedUser={service.updUser}
        />
    );

    const renderStatus = () => <div className={styles.status}>{ServiceStatusLabel[service.status]}</div>;

    const renderEditButton = () => (
        <div>
            {(auth.rolle === 'GF' || auth.rolle === 'ID') && (
                <PrimaryButton
                    className={viewMode === 'short' ? formStyles.smallButton : ''}
                    onClick={() => {
                        editService();
                    }}
                    text="Bearbeiten"
                />
            )}
        </div>
    );

    const containerClassName = classnames(viewMode === 'short' ? formStyles.listItemShort : formStyles.listItem, {
        [formStyles.serviceInArbeit]: service.status === 0,
        [formStyles.serviceVersendet]: service.status === 1,
        [formStyles.serviceAngenommen]: service.status === 2,
    });

    if (viewMode === 'short') {
        return (
            <div className={containerClassName}>
                <div className={styles.shortLine}>
                    <div className={formStyles.shortValue}>
                        <strong>{service.nr}</strong>
                    </div>
                    <div className={formStyles.shortValue}>
                        <strong>{formatSmDate(service.datum)}</strong>
                    </div>
                    {withKunde ? (
                        <div
                            className={classnames(formStyles.listTitleShort, formStyles.clickable)}
                            onClick={() => {
                                history.push('/kunden/' + kunde.nr);
                            }}
                        >
                            {`${kunde.name1} ${kunde.name2} (${kunde.nr})`}
                        </div>
                    ) : (
                        <div />
                    )}

                    <div className={formStyles.shortValue}>
                        <strong>{service.aktText}</strong>
                    </div>

                    {renderStatus()}
                    {renderEditButton()}
                </div>

                <div>
                    <ServicePositionen
                        kunde={kunde}
                        service={service}
                        sNr={service.nr}
                        servicePositionen={servicePositionen}
                        wiedervorlagen={wiedervorlagen}
                        viewMode={viewMode}
                        withHeader={withHeader}
                        refetch={refetch}
                    />
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={containerClassName}>
                <div className={formStyles.topBlock}>
                    <div>
                        <div
                            className={classnames(formStyles.listTitle, formStyles.clickable)}
                            onClick={() => {
                                history.push('/kunden/' + kunde.nr);
                                // openDetails();
                            }}
                        >
                            Service {service.nr} - {`${kunde.name1} ${kunde.name2} (${kunde.nr})`}
                        </div>
                    </div>
                    {renderStatus()}
                    {renderEditButton()}
                </div>
                <div className={formStyles.fourColumnsBlock}>
                    <div className={formStyles.firstColumn}>
                        <div className={formStyles.line}>
                            {kunde.plz} {kunde.ort}, {kunde.strasse} {kunde.land && `(${kunde.land})`}
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Ansprechpartner:</div>
                            <div className={formStyles.dataValue}>{service.kaName}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Service-Nr:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{service.nr}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Typ:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{ServiceTypLabel[service.typ]}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Auftrag-Nr:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{service.auNr}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Datum:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{formatSmDate(service.datum)}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Betreuer:</div>
                            <div className={formStyles.dataValue}>
                                <strong>
                                    {service.betreuer &&
                                        (betreuerByNr[service.betreuer]
                                            ? betreuerByNr[service.betreuer].name || service.betreuer
                                            : service.betreuer)}
                                </strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Tel.-Betreuer:</div>
                            <div className={formStyles.dataValue}>
                                <strong>
                                    {service.telBetreuer
                                        ? betreuerByNr[service.telBetreuer]
                                            ? betreuerByNr[service.telBetreuer].name || service.telBetreuer
                                            : service.telBetreuer
                                        : '-'}
                                </strong>
                            </div>
                        </div>
                    </div>
                    <div className={formStyles.secondColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Betreff E-Akt:</div>
                            <div className={formStyles.dataValue}>{service.aktText}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Text:</div>
                            <div className={formStyles.dataValue}>{nl2br(service.txt)}</div>
                        </div>
                    </div>
                    <div className={formStyles.thirdColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Ext. Doku:</div>
                            <div className={formStyles.dataValue}>{service.extDoku ? 'Ja' : 'Nein'}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferbed.:</div>
                            <div className={formStyles.dataValue}>
                                {service.liefBed
                                    ? textbausteineByKzNr[11][service.liefBed]
                                        ? textbausteineByKzNr[11][service.liefBed].name || service.liefBed
                                        : service.liefBed
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferzeit:</div>
                            <div className={formStyles.dataValue}>
                                {service.liefZeit
                                    ? textbausteineByKzNr[14][service.liefZeit]
                                        ? textbausteineByKzNr[14][service.liefZeit].name || service.liefZeit
                                        : service.liefZeit
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferdatum.:</div>
                            <div className={formStyles.dataValue}>
                                {service.liefDat && (formatSmDate(service.liefDat, true) || '-')}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Liefer-KW.:</div>
                            <div className={formStyles.dataValue}>{service.liefKw ? service.liefKw : '-'}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Zahlungsbed.:</div>
                            <div className={formStyles.dataValue}>
                                {service.zahlBed
                                    ? skontodefinitionenByArt[service.zahlBed]
                                        ? skontodefinitionenByArt[service.zahlBed].text || service.zahlBed
                                        : service.zahlBed
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>MwSt:</div>
                            <div className={formStyles.dataValue}>{formatPercent(service.mwstCode)}</div>
                        </div>
                    </div>
                    <div className={formStyles.fourthColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Bemerkungen (beim Kunden):</div>
                            <div className={formStyles.dataValue}>{nl2br(service?.kunde?.bemerkung)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Lieferbemerkung (beim Kunden):</div>
                            <div className={formStyles.dataValue}>{nl2br(service?.kunde?.liefBem)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Zusatzvereinbarung:</div>
                            <div className={formStyles.dataValue}>{nl2br(service?.zusatz)}</div>
                        </div>
                    </div>
                </div>
                <div>
                    <ServicePositionen
                        kunde={kunde}
                        service={service}
                        sNr={service.nr}
                        servicePositionen={servicePositionen}
                        wiedervorlagen={wiedervorlagen}
                        viewMode={viewMode}
                        refetch={refetch}
                    />
                </div>
                <div className={formStyles.threeColumnsBlock}>
                    <div className={formStyles.firstColumn}>
                        <div className={formStyles.sumLine}>Summe Netto: {formatMoney(service.sNetto)}</div>
                    </div>
                    <div className={formStyles.secondColumn}>
                        <div className={formStyles.sumLine}>Summe Brutto: {formatMoney(service.sBrutto)}</div>
                    </div>
                    <div className={formStyles.thirdColumn}>
                        <div className={formStyles.sumLine}>Summe MwSt.: {formatMoney(service.sMwst)}</div>
                    </div>
                </div>
                {renderTimestamps()}
            </div>
        </>
    );
};
